import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PropertyStoreContext } from "../../../contexts/PropertyStoreContext";
import usePropertyPickerOptions from "../../../hooks/usePropertyPickerOptions";
import { formatPropertyLabel } from "../../../utils/propertyLabel";

/**
 * Sub-hook of `useOfferTypeState` owning property-selection state,
 * derived property lists, and the effects that keep local selectedProperties
 * in sync with the active context property and the persisted promo field.
 *
 * Exposes the same field names the parent orchestrator used to expose
 * directly so existing consumers continue to work unchanged.
 */
export function useOfferTypePropertySelection({
  promo,
  onFieldChange,
  isEdit = false,
  isDuplicate = false,
  // Per-property rooms/rate-plans cache owned by the orchestrator; cleared
  // here when the header property changes in create mode.
  roomsRateplansByPropertyRef,
}) {
  const { selectedProperty, properties, isPilotHotel } =
    useContext(PropertyStoreContext);

  // Get selected properties — default to current selected property
  const [selectedProperties, setSelectedProperties] = useState(() => {
    if (promo?.selectedProperties && promo.selectedProperties.length > 0) {
      return promo.selectedProperties;
    }
    return selectedProperty?.propertyId ? [selectedProperty.propertyId] : [];
  });

  // Refs
  // Exposed externally: wraps the TagPicker container element
  const hotelSelectContainerRef = useRef(null);
  // Internal: tracks the previous property ID to detect context switches
  const prevPropertyIdRef = useRef(selectedProperty?.propertyId);

  // Properties dropdown options — searched by name through the API (20 at a
  // time), like the header property selector.
  const propertyToOption = useCallback(
    (prop) => ({
      label: formatPropertyLabel(prop),
      value: prop.propertyId,
      // Carried so each selected property's CRS can be resolved for the
      // calculation-method lock. Pickers ignore unknown option fields.
      crsId: prop.crsId,
    }),
    []
  );

  // `properties` only holds the first catalog page, but the header selection is
  // pre-selected here and may sit beyond it — PropertyStoreContext restores such
  // a selection from localStorage, not from that list. The picker renders a tag
  // only for a value it can resolve to an option, so without seeding the header
  // property explicitly the id is selected with nothing to render and the tag
  // stays invisible until the user happens to search that property up.
  const propertySeed = useMemo(() => {
    const known = properties || [];
    const headerId = selectedProperty?.propertyId;
    if (!headerId || known.some((p) => p.propertyId === headerId)) {
      return known;
    }
    return [...known, selectedProperty];
  }, [properties, selectedProperty]);

  /**
   * A promo can only span properties of the same pilot status as the header
   * property: pilot hotels run derived-rate promos, non-pilot hotels do not, and
   * the two cannot be mixed in one promo.
   *
   * Applied client-side to every page of search results, because the property
   * search endpoint cannot filter by pilot status.
   *
   * Two cases deliberately do NOT filter, both erring towards showing a
   * property rather than hiding it:
   *  - `isPilotHotel` is still undefined: PropertyStoreContext resolves it with
   *    a separate per-property call, so it is unknown for the first frames.
   *    Filtering on it while unresolved would show the *wrong* set (undefined is
   *    falsy, i.e. "non-pilot") and then swap it out under the user.
   *  - the property carries no `pilotProperty` flag at all: hiding everything
   *    would leave an inexplicably empty dropdown, which is worse than an
   *    unfiltered one.
   */
  const filterPropertyByPilot = useCallback(
    (prop) => {
      if (isPilotHotel == null) return true;
      if (prop?.pilotProperty == null) return true;
      return isPilotHotel ? !!prop.pilotProperty : !prop.pilotProperty;
    },
    [isPilotHotel]
  );

  const {
    options: propertyOptions,
    onSearch: onPropertiesSearch,
    reset: onPropertiesReset,
  } = usePropertyPickerOptions({
    selectedValues: selectedProperties,
    toOption: propertyToOption,
    filterProperty: filterPropertyByPilot,
    seedProperties: propertySeed,
  });

  // Filtered property options for hotel select (only those already selected)
  const filteredPropertyOptionsForHotel = useMemo(
    () =>
      propertyOptions.filter((option) =>
        selectedProperties.includes(option.value)
      ),
    [propertyOptions, selectedProperties]
  );

  // Effects

  // Sync selectedProperties from promo → local state when promo is loaded async (e.g. edit mode).
  // Also syncs local state → promo in create mode when local has values but promo doesn't.
  useEffect(() => {
    if (
      promo?.selectedProperties &&
      promo.selectedProperties.length > 0 &&
      JSON.stringify(promo.selectedProperties) !==
        JSON.stringify(selectedProperties)
    ) {
      setSelectedProperties(promo.selectedProperties);
    } else if (selectedProperties.length > 0 && !promo?.selectedProperties) {
      // Sync local state → promo (create mode or reset)
      onFieldChange("selectedProperties", selectedProperties);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.selectedProperties]);

  // Header property changed while the form is open. In create mode the form
  // now targets the new property, so it starts from that property's own
  // selection — appending the new id (the old behaviour) left the previous
  // property's rate plans in allowedRateRoomCodes. Edit/duplicate keep the
  // selection loaded from the promo being edited.
  useEffect(() => {
    const currentPropertyId = selectedProperty?.propertyId;
    if (!currentPropertyId || currentPropertyId === prevPropertyIdRef.current) {
      return;
    }
    prevPropertyIdRef.current = currentPropertyId;

    if (isEdit || isDuplicate) {
      return;
    }

    setSelectedProperties([currentPropertyId]);
    onFieldChange("selectedProperties", [currentPropertyId]);
    onFieldChange("allowedRateRoomCodesByProperty", {});
    // Left empty on purpose: the page refetches rooms/rate plans for the new
    // property and useCreatePromoState rebuilds the default selection from
    // them once they land.
    onFieldChange("allowedRateRoomCodes", []);
    if (roomsRateplansByPropertyRef) {
      roomsRateplansByPropertyRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.propertyId, isEdit, isDuplicate]);

  return {
    // Context values — re-exported so OfferType.jsx can destructure from orchestrator
    selectedProperty,
    properties,
    isPilotHotel,

    // State
    selectedProperties,
    setSelectedProperties,

    // Refs
    hotelSelectContainerRef,

    // Computed values
    propertyOptions,
    onPropertiesSearch,
    onPropertiesReset,
    filteredPropertyOptionsForHotel,
  };
}
